import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, Edit, CreditCard, ChevronDown, Copy, Mail, MessageCircle, Eye, X } from 'lucide-react';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import PaymentModal from '../components/PaymentModal';
import { useAuth } from '../hooks/useAuth';
import { api, formatCurrency, calculateInvoiceTotal, API_BASE } from '../lib/api';
import { generateInvoicePDF, generateInvoicePDFBase64, generateInvoicePDFBlobUrl } from '../lib/pdf';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isViewer } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchInvoice = () => {
    api.get(`/invoices/${id}`).then(res => setInvoice(res.data));
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  if (!invoice) {
    return <Layout title="Invoice"><div className="text-center py-8 font-body text-text-secondary">Loading...</div></Layout>;
  }

  const { subtotal, gst, tds, total } = calculateInvoiceTotal(invoice);
  const paid = (invoice.payments || []).reduce((s, p) => s + parseFloat(p.amount), 0);
  const balance = total - paid;

  const handleDownload = async () => {
    if (invoice.source === 'uploaded' && invoice.uploaded_pdf_path) {
      window.open(`${API_BASE}/files/${invoice.uploaded_pdf_path}`, '_blank');
      return;
    }
    await generateInvoicePDF(invoice);
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      if (invoice.source === 'uploaded' && invoice.uploaded_pdf_path) {
        setPreviewUrl(`${API_BASE}/files/${invoice.uploaded_pdf_path}`);
      } else {
        const url = await generateInvoicePDFBlobUrl(invoice);
        setPreviewUrl(url);
      }
    } catch (e) {
      alert('Preview failed: ' + e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const changeStatus = async (newStatus) => {
    setStatusOpen(false);
    await api.patch(`/invoices/${id}/status`, { status: newStatus });
    fetchInvoice();
  };

  const duplicateInvoice = async () => {
    if (!window.confirm('Create a duplicate of this invoice as a new draft?')) return;
    const res = await api.post(`/invoices/${id}/duplicate`);
    navigate(`/invoices/${res.data.id}/edit`);
  };

  const openEmailModal = () => {
    setEmailRecipient(invoice.client?.email || '');
    setShowEmailModal(true);
  };

  const sendEmail = async () => {
    if (!emailRecipient.trim()) { alert('Recipient email required'); return; }
    setSending(true);
    try {
      const pdfBase64 = await generateInvoicePDFBase64(invoice);
      await api.post(`/invoices/${id}/send-email`, {
        recipient_email: emailRecipient,
        pdf_base64: pdfBase64,
        subject: `Invoice ${invoice.invoice_no} from ${invoice.company?.full_name || 'Wolf Media'}`,
      });
      alert('Email sent via Make.com webhook');
      setShowEmailModal(false);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setSending(false); }
  };

  const sendFollowup = async (channel) => {
    if (!window.confirm(`Send ${channel} follow-up for this invoice?`)) return;
    setSending(true);
    try {
      await api.post(`/invoices/${id}/send-followup`, { channel });
      alert(`${channel} follow-up triggered`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setSending(false); }
  };

  return (
    <Layout
      title={invoice.invoice_no}
      action={
        <div className="flex gap-2 flex-wrap">
          {!isViewer && (
            <>
              <button data-testid="duplicate-invoice-button" onClick={duplicateInvoice} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-3 py-2 font-heading text-sm transition-colors flex items-center">
                <Copy className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Duplicate
              </button>
              <button data-testid="send-email-button" onClick={openEmailModal} className="bg-transparent text-success border border-success hover:bg-cream px-3 py-2 font-heading text-sm transition-colors flex items-center">
                <Mail className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Email
              </button>
              <button data-testid="send-followup-whatsapp-button" onClick={() => sendFollowup('whatsapp')} disabled={sending} className="bg-transparent text-success border border-success hover:bg-cream px-3 py-2 font-heading text-sm transition-colors flex items-center disabled:opacity-50">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> WhatsApp
              </button>
              <button data-testid="record-payment-button" onClick={() => setShowPaymentModal(true)} className="bg-transparent text-maroon border border-maroon hover:bg-cream px-3 py-2 font-heading text-sm transition-colors flex items-center">
                <CreditCard className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Record Payment
              </button>
            </>
          )}
          <button data-testid="preview-pdf-button" onClick={openPreview} disabled={previewLoading} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-3 py-2 font-heading text-sm transition-colors flex items-center disabled:opacity-50">
            <Eye className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> {previewLoading ? 'Loading…' : 'Preview'}
          </button>
          <button data-testid="download-pdf-button" onClick={handleDownload} className="bg-transparent text-maroon border border-maroon hover:bg-cream px-3 py-2 font-heading text-sm transition-colors flex items-center">
            <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> PDF
          </button>
          {!isViewer && (
            <button data-testid="edit-invoice-button" onClick={() => navigate(`/invoices/${id}/edit`)} className="bg-maroon text-white hover:bg-maroon-hover px-3 py-2 font-heading text-sm transition-colors flex items-center">
              <Edit className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Edit
            </button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Total</p>
          <p className="font-numbers text-2xl text-text-primary tabular-nums">{formatCurrency(total)}</p>
        </div>
        <div className="bg-surface border border-border p-6">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Paid</p>
          <p className="font-numbers text-2xl text-success tabular-nums">{formatCurrency(paid)}</p>
        </div>
        <div className="bg-surface border border-border p-6 border-l-2 border-l-maroon">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Balance Due</p>
          <p className="font-numbers text-2xl text-maroon tabular-nums">{formatCurrency(balance)}</p>
        </div>
      </div>

      <div className="bg-surface border border-border p-8 mb-6">
        <div className="flex justify-between mb-8">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Billed To</p>
            <p className="font-heading text-xl text-text-primary">{invoice.client?.name}</p>
            <p className="font-body text-sm text-text-secondary mt-1">{invoice.client?.address}</p>
            {invoice.client?.gstin && <p className="font-body text-sm text-text-secondary mt-1">GSTIN: {invoice.client.gstin}</p>}
          </div>
          <div className="text-right">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Status</p>
            <div className="relative inline-block">
              {isViewer ? (
                <StatusBadge status={invoice.status} />
              ) : (
                <button
                  data-testid="status-change-button"
                  onClick={() => setStatusOpen(!statusOpen)}
                  className="flex items-center gap-2"
                >
                  <StatusBadge status={invoice.status} />
                  <ChevronDown className="w-4 h-4 text-text-secondary" strokeWidth={1} />
                </button>
              )}
              {statusOpen && (
                <div data-testid="status-dropdown" className="absolute right-0 mt-2 bg-cream border border-border z-10 min-w-[140px]">
                  {['pending', 'paid', 'partial', 'overdue'].map(s => (
                    <button
                      key={s}
                      data-testid={`status-option-${s}`}
                      onClick={() => changeStatus(s)}
                      className="block w-full text-left px-4 py-2 font-body text-sm uppercase tracking-widest text-text-secondary hover:text-maroon hover:bg-surface transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mt-4 mb-1">Date / Due</p>
            <p className="font-body text-sm text-text-primary">{invoice.date} → {invoice.due_date}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8 pb-6 border-b border-border">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Service</p>
            <p className="font-body text-base text-text-primary">{invoice.service || '—'}</p>
          </div>
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Period</p>
            <p className="font-body text-base text-text-primary">{invoice.period || '—'}</p>
          </div>
        </div>

        <table className="w-full mb-6">
          <thead>
            <tr>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Description</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Qty</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Rate</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, i) => (
              <tr key={i} className="border-b border-border">
                <td className="py-3 font-body text-sm text-text-primary">{item.desc}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{item.qty}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(item.rate)}</td>
                <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(item.amt)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="ml-auto max-w-sm space-y-2">
          <div className="flex justify-between font-body text-sm"><span className="text-text-secondary">Subtotal</span><span className="font-numbers tabular-nums">{formatCurrency(subtotal)}</span></div>
          <div className="flex justify-between font-body text-sm"><span className="text-text-secondary">GST ({invoice.gst_percent}%)</span><span className="font-numbers tabular-nums">{formatCurrency(gst)}</span></div>
          <div className="flex justify-between font-body text-sm"><span className="text-text-secondary">TDS ({invoice.tds_percent}%)</span><span className="font-numbers tabular-nums">- {formatCurrency(tds)}</span></div>
          <div className="flex justify-between pt-2 border-t border-border"><span className="font-heading text-lg">Total</span><span className="font-numbers text-xl tabular-nums text-maroon">{formatCurrency(total)}</span></div>
        </div>

        {invoice.notes && (
          <div className="mt-8 pt-6 border-t border-border">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Notes</p>
            <p className="font-body text-sm text-text-primary">{invoice.notes}</p>
          </div>
        )}
      </div>

      <div data-testid="payment-history" className="bg-surface border border-border p-6">
        <h3 className="font-heading text-xl font-light mb-4">Payment History</h3>
        {invoice.payments?.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Mode</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Bank</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Reference</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.payments.map(p => (
                <tr key={p.id} className="border-b border-border">
                  <td className="py-3 font-body text-sm">{p.date}</td>
                  <td className="py-3 font-body text-sm">{p.mode}</td>
                  <td className="py-3 font-body text-sm">{p.bank_label}</td>
                  <td className="py-3 font-body text-sm text-text-secondary">{p.reference || '—'}</td>
                  <td className="py-3 font-numbers text-sm text-success text-right tabular-nums">{formatCurrency(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-center py-8 font-body text-sm text-text-secondary">No payments recorded</p>
        )}
      </div>

      {showPaymentModal && (
        <PaymentModal
          invoiceId={id}
          balanceDue={balance}
          onClose={() => setShowPaymentModal(false)}
          onSaved={() => { setShowPaymentModal(false); fetchInvoice(); }}
        />
      )}

      {showEmailModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 fade-up">
          <div className="bg-cream border border-border max-w-md w-full p-8">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— Email Invoice</p>
            <h2 className="font-heading text-2xl font-medium mb-4">Send {invoice.invoice_no}</h2>
            <p className="font-body text-xs text-text-secondary mb-4">
              The invoice PDF + data will be posted to your Make.com webhook. Your scenario handles the email send.
            </p>
            <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Recipient Email</label>
            <input
              data-testid="email-recipient-input"
              type="email"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors mb-6"
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowEmailModal(false)} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading text-sm transition-colors">Cancel</button>
              <button data-testid="confirm-send-email" onClick={sendEmail} disabled={sending} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm transition-colors disabled:opacity-50">{sending ? 'Sending...' : 'Send'}</button>
            </div>
          </div>
        </div>
      )}
      {previewUrl && (
        <div data-testid="pdf-preview-modal" className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 fade-up">
          <div className="bg-cream border border-border w-[min(900px,95vw)] h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-0.5">— Preview</p>
                <h2 className="font-heading text-base font-medium">{invoice.invoice_no}</h2>
              </div>
              <div className="flex gap-2">
                <button data-testid="preview-download-button" onClick={handleDownload} className="bg-maroon text-white hover:bg-maroon-hover px-4 py-2 font-heading text-sm transition-colors flex items-center">
                  <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Download
                </button>
                <button data-testid="preview-close-button" onClick={closePreview} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-3 py-2 font-heading text-sm transition-colors flex items-center">
                  <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <iframe
              title="Invoice PDF Preview"
              src={previewUrl}
              className="flex-1 w-full bg-white"
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default InvoiceDetail;
